'use client'

import { useState } from 'react'
import Link from 'next/link'
import { NavItem } from './NavItem'
import { UserMenu } from './UserMenu'
import { useAuth } from '@/contexts/AuthContext'

// Icons as components for cleaner code
const SearchIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
)

const PulseIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
  </svg>
)

const UsersIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
)

const SparklesIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
  </svg>
)

const BookmarkIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
  </svg>
)

const PortfolioIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
  </svg>
)

const ChevronIcon = ({ collapsed }: { collapsed: boolean }) => (
  <svg
    className={`w-4 h-4 transition-transform ${collapsed ? 'rotate-180' : ''}`}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
)

export function Sidebar() {
  const [collapsed, setCollapsed] = useState(false)
  const { user } = useAuth()

  const watchlistCount = user ? user.watchlist.artists + user.watchlist.lots : 0

  return (
    <aside
      className={`sidebar hidden md:flex flex-col h-screen sticky top-0 border-r border-border-subtle bg-card transition-all duration-300 ${
        collapsed ? 'w-16' : 'w-64'
      }`}
    >
      {/* Header with logo and collapse toggle */}
      <div className={`flex items-center justify-between p-4 border-b border-border-subtle ${collapsed ? 'px-3' : ''}`}>
        <Link href="/" className={`flex items-baseline gap-0.5 ${collapsed ? 'hidden' : ''}`}>
          <span className="font-display text-2xl font-light text-text-primary">salle</span>
          <span className="text-sm text-text-tertiary">.art</span>
        </Link>

        {collapsed && (
          <Link href="/" className="mx-auto">
            <span className="font-display text-xl font-light text-text-primary">s</span>
          </Link>
        )}

        <button
          onClick={() => setCollapsed(!collapsed)}
          className={`p-1.5 rounded-lg hover:bg-page text-text-tertiary hover:text-text-secondary transition-colors ${
            collapsed ? 'mx-auto mt-2' : ''
          }`}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <ChevronIcon collapsed={collapsed} />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        <NavItem
          href="/app/search"
          icon={<SearchIcon />}
          label="Search"
          collapsed={collapsed}
        />

        <div className="h-px bg-border-subtle my-3" />

        <NavItem
          href="/app/market"
          icon={<PulseIcon />}
          label="Market"
          badge="Live"
          badgeType="live"
          collapsed={collapsed}
        />

        <NavItem
          href="/app/artists"
          icon={<UsersIcon />}
          label="Artists"
          collapsed={collapsed}
        />

        <NavItem
          href="/app/insights"
          icon={<SparklesIcon />}
          label="Insights"
          badge={3}
          badgeType="count"
          collapsed={collapsed}
        />

        <NavItem
          href="/app/watchlist"
          icon={<BookmarkIcon />}
          label="Watchlist"
          badge={watchlistCount > 0 ? watchlistCount : undefined}
          badgeType="count"
          collapsed={collapsed}
        />

        <NavItem
          href="/app/portfolio"
          icon={<PortfolioIcon />}
          label="Portfolio"
          collapsed={collapsed}
        />
      </nav>

      {/* User section */}
      <div className={`p-3 border-t border-border-subtle ${collapsed ? 'px-2' : ''}`}>
        <UserMenu collapsed={collapsed} />
      </div>
    </aside>
  )
}
